/**
 * firewall-rule-model.ts
 *
 * Pure data layer for the Firewall Rules editor:
 *   - Field metadata (labels, operators, value kinds) that drive the condition builder
 *   - Operator/action constant tables
 *   - Form types (FForm / FGroup / FCond) and their builder functions
 *   - Wire-format converters (toForm / toWire / ruleInput)
 *   - Rule-summary text helpers
 *   - Template presets
 *
 * No JSX — safe to import from non-React modules.
 */

import type { FirewallRule, FwCondition } from '@/api/manticore';

// ── Field / operator metadata (drives the condition editor) ────────────────
export type ValueKind = 'text' | 'number' | 'ip' | 'select' | 'boolean' | 'header';
export interface FieldMeta {
  label: string;
  kind: ValueKind;
  ops: string[];
  options?: string[];
}

export const TEXT_OPS = ['eq', 'ne', 'contains', 'not_contains', 'starts_with', 'ends_with', 'matches', 'not_matches', 'wildcard', 'exists', 'not_exists', 'is_empty', 'is_not_empty'];
export const SET_OPS = ['eq', 'ne', 'in', 'not_in'];
export const NUM_OPS = ['eq', 'ne', 'ge', 'gt', 'le', 'lt'];
export const NO_VALUE_OPS = ['exists', 'not_exists', 'is_empty', 'is_not_empty'];
const METHODS = ['GET', 'POST', 'PUT', 'PATCH', 'DELETE', 'HEAD', 'OPTIONS', 'CONNECT', 'TRACE'];
const COUNTRIES = ['US', 'VN', 'GB', 'DE', 'FR', 'JP', 'CN', 'KR', 'SG', 'AU', 'CA', 'BR', 'IN', 'RU'];

export const FIELD_META: Record<string, FieldMeta> = {
  ip_src: { label: 'Client IP', kind: 'ip', ops: ['eq', 'ne', 'ip_in', 'in', 'not_in', 'exists', 'not_exists'] },
  ip_geoip_country: { label: 'Country', kind: 'select', ops: SET_OPS, options: COUNTRIES },
  ip_geoip_asnum: { label: 'ASN', kind: 'number', ops: [...NUM_OPS, 'in', 'not_in'] },
  http_request_method: { label: 'HTTP Method', kind: 'select', ops: SET_OPS, options: METHODS },
  http_request_uri: { label: 'URI (path+query)', kind: 'text', ops: TEXT_OPS },
  http_request_uri_path: { label: 'URI Path', kind: 'text', ops: TEXT_OPS },
  http_request_uri_query: { label: 'Query String', kind: 'text', ops: TEXT_OPS },
  http_request_full_uri: { label: 'Full URI', kind: 'text', ops: TEXT_OPS },
  http_host: { label: 'Host', kind: 'text', ops: TEXT_OPS },
  http_user_agent: { label: 'User-Agent', kind: 'text', ops: TEXT_OPS },
  http_referer: { label: 'Referer', kind: 'text', ops: TEXT_OPS },
  http_request_headers_content_type: { label: 'Content-Type', kind: 'text', ops: TEXT_OPS },
  http_request_headers: { label: 'Header (by name)', kind: 'header', ops: TEXT_OPS },
  ssl: { label: 'Is HTTPS', kind: 'boolean', ops: ['eq'] },
  threat_score: { label: 'Threat score', kind: 'number', ops: NUM_OPS },
};
export const FIELDS = Object.keys(FIELD_META);

export const OP_LABELS: Record<string, string> = {
  eq: 'equals', ne: 'does not equal', contains: 'contains', not_contains: 'does not contain',
  starts_with: 'starts with', ends_with: 'ends with', matches: 'matches', not_matches: 'does not match',
  wildcard: 'wildcard', in: 'in list', not_in: 'not in list', ip_in: 'in CIDR',
  ge: '≥', gt: '>', le: '≤', lt: '<', exists: 'exists', not_exists: 'is absent',
  is_empty: 'is empty', is_not_empty: 'is not empty',
};
export const TRANSFORMS = ['lowercase', 'uppercase', 'url_decode', 'url_decode_uni', 'html_entity_decode', 'base64_decode', 'hex_decode', 'remove_whitespace'];

export const ACTIONS = [
  { value: 'block', label: 'Block', tone: 'danger' as const },
  { value: 'allow', label: 'Allow', tone: 'success' as const },
  { value: 'log', label: 'Log only', tone: 'info' as const },
  { value: 'bypass_waf', label: 'Bypass WAF', tone: 'accent' as const },
  { value: 'redirect', label: 'Redirect', tone: 'warning' as const },
  { value: 'timed_block', label: 'Timed block', tone: 'primary' as const },
];
export const actionTone = (a: string) => ACTIONS.find((x) => x.value === a)?.tone ?? 'neutral';
export const actionLabel = (a: string) => ACTIONS.find((x) => x.value === a)?.label ?? a;

// ── Form types ─────────────────────────────────────────────────────────────
export interface FCond { _id: string; field: string; field_key: string; operator: string; value: string; negate: boolean; case_insensitive: boolean; transforms: string[] }
export interface FGroup { _id: string; conditions: FCond[] }
export interface FForm {
  id?: string;
  name: string;
  description: string;
  priority: number;
  enabled: boolean;
  action: string;
  status_code: number;
  redirect_url: string;
  duration: number;
  tags: string[];
  groups: FGroup[];
}

// ── Form builders ──────────────────────────────────────────────────────────
let _f = 0;
export const lid = () => `f${++_f}`;
export const mkCond = (p: Partial<FCond> = {}): FCond => ({ _id: lid(), field: 'http_request_method', field_key: '', operator: 'eq', value: 'GET', negate: false, case_insensitive: true, transforms: [], ...p });
export const mkGroup = (conditions: FCond[] = [mkCond()]): FGroup => ({ _id: lid(), conditions });
export const blankForm = (priority: number): FForm => ({ name: '', description: '', priority, enabled: true, action: 'block', status_code: 403, redirect_url: '', duration: 60, tags: [], groups: [mkGroup()] });

/** Deserialize a server FirewallRule into the grouped editor form model. */
export function toForm(r: FirewallRule): FForm {
  const groups = new Map<number, FCond[]>();
  (r.conditions || []).forEach((c) => {
    const g = c.condition_group ?? 0;
    if (!groups.has(g)) groups.set(g, []);
    groups.get(g)!.push(mkCond({
      field: c.field || 'http_request_method', field_key: c.field_key || '', operator: c.operator || 'eq',
      value: c.value || '', negate: !!c.negate, case_insensitive: c.case_insensitive ?? true,
      transforms: Array.isArray(c.transformations) ? c.transformations : [],
    }));
  });
  const ap = r.action_params || {};
  return {
    id: r.id, name: r.name || '', description: r.description || '', priority: r.priority ?? 100,
    enabled: r.enabled ?? true, action: r.action || 'block', status_code: Number(ap.status_code) || 403,
    redirect_url: (ap.url as string) || '', duration: Number(ap.duration) || 60,
    tags: Array.isArray(r.tags) ? r.tags : [],
    groups: groups.size ? [...groups.entries()].sort((a, b) => a[0] - b[0]).map(([, cs]) => ({ _id: lid(), conditions: cs })) : [mkGroup()],
  };
}

export type RuleInput = Omit<FirewallRule, 'id' | 'hits' | 'last_hit'> & { id?: string };

/** Serialize the editor form model back to the wire shape for the API. */
export function toWire(f: FForm): RuleInput {
  const conditions: FwCondition[] = [];
  f.groups.forEach((g, gi) =>
    g.conditions.forEach((c, ci) =>
      conditions.push({
        condition_group: gi, field: c.field,
        field_key: c.field === 'http_request_headers' ? c.field_key || null : null,
        operator: c.operator, value: NO_VALUE_OPS.includes(c.operator) ? '' : c.value,
        negate: c.negate, transformations: c.transforms, case_insensitive: c.case_insensitive, sequence: ci,
      }),
    ),
  );
  const action_params: Record<string, unknown> =
    f.action === 'redirect' ? { url: f.redirect_url, status_code: f.status_code }
      : f.action === 'timed_block' ? { status_code: f.status_code, duration: f.duration }
        : f.action === 'block' ? { status_code: f.status_code } : {};
  return { id: f.id, name: f.name.trim(), description: f.description, priority: f.priority, enabled: f.enabled, action: f.action, action_params, tags: f.tags, conditions };
}

/** Build a minimal wire input from an existing rule with selective overrides (e.g. toggle enabled). */
export function ruleInput(r: FirewallRule, overrides: Partial<RuleInput>): RuleInput {
  return { id: r.id, name: r.name, description: r.description, priority: r.priority, enabled: r.enabled, action: r.action, action_params: r.action_params || {}, tags: r.tags || [], conditions: r.conditions || [], ...overrides };
}

// ── Summary helpers ────────────────────────────────────────────────────────
export const condText = (c: FCond): string => {
  const m = FIELD_META[c.field];
  const lbl = m?.label ?? c.field;
  if (NO_VALUE_OPS.includes(c.operator)) return `${c.negate ? 'NOT ' : ''}${lbl} ${OP_LABELS[c.operator]}`;
  if (m?.kind === 'boolean') return `${c.value === 'true' ? '' : 'NOT '}${lbl}`;
  return `${c.negate ? 'NOT ' : ''}${lbl} ${OP_LABELS[c.operator] ?? c.operator} ${c.value || '…'}`;
};
export function ruleSummary(f: FForm): string {
  const parts = f.groups.map((g) => g.conditions.map(condText).join(' AND ')).filter(Boolean);
  if (!parts.length) return 'Applies to all requests.';
  const joined = parts.length > 1 ? parts.map((p) => `(${p})`).join(' OR ') : parts[0];
  return `When ${joined}, ${actionLabel(f.action).toLowerCase()} the request.`;
}

// ── Template presets ───────────────────────────────────────────────────────
export const TEMPLATES: { name: string; make: (p: number) => FForm }[] = [
  { name: 'Block known scanners', make: (p) => ({ ...blankForm(p), name: 'Block known scanners', action: 'block', tags: ['scanner'], groups: [mkGroup([mkCond({ field: 'http_user_agent', operator: 'matches', value: '(?i)(nikto|sqlmap|nmap|masscan)' })])] }) },
  { name: 'Allow internal IPs (skip WAF)', make: (p) => ({ ...blankForm(p), name: 'Allow internal network', action: 'allow', tags: ['allowlist', 'internal'], groups: [mkGroup([mkCond({ field: 'ip_src', operator: 'ip_in', value: '10.0.0.0/8' })])] }) },
  { name: 'Protect /admin by country', make: (p) => ({ ...blankForm(p), name: 'Block /admin except trusted', action: 'block', tags: ['admin', 'geo'], groups: [mkGroup([mkCond({ field: 'http_request_uri_path', operator: 'starts_with', value: '/admin' }), mkCond({ field: 'ip_geoip_country', operator: 'not_in', value: 'US,VN' })])] }) },
  { name: 'Block WordPress admin probes', make: (p) => ({ ...blankForm(p), name: 'Block wp-admin probes', action: 'block', tags: ['wordpress'], groups: [mkGroup([mkCond({ field: 'http_request_uri_path', operator: 'contains', value: '/wp-admin' })])] }) },
  { name: 'Timed block login brute force', make: (p) => ({ ...blankForm(p), name: 'Timed block brute force', action: 'timed_block', duration: 600, tags: ['bruteforce', 'login'], groups: [mkGroup([mkCond({ field: 'http_request_uri_path', operator: 'eq', value: '/login' }), mkCond({ field: 'threat_score', operator: 'gt', value: '60' })])] }) },
  { name: 'Log SQLi probes (detect only)', make: (p) => ({ ...blankForm(p), name: 'Log SQL injection probes', action: 'log', tags: ['sqli', 'observe'], groups: [mkGroup([mkCond({ field: 'http_request_uri_query', operator: 'contains', value: 'union select' })])] }) },
];
